'use client';

import { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  X,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Minimize2,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { QrOverlay } from '@/components/qr-overlay';
import { UploadProgress } from '@/components/upload-progress';
import { PanelResizeHandle } from '@/components/panel-resize-handle';
import { usePanelWidth } from '@/hooks/use-panel-width';
import type { QrOverlayStatus } from '@/lib/qr/qr-overlay-styles';
import type { QrBboxData } from '@/lib/qr/decode';
import type { QrReviewPageRecord } from '../types';

// ─── Props ───────────────────────────────────────────────────────────────────

interface QrReviewImageViewerProps {
  mainImageBlobUrl: string | null;
  selectedPage: QrReviewPageRecord | null;
  selectedPageIndex: number;
  totalPages: number;
  onPrev: () => void;
  onNext: () => void;
  // Zoom/pan from useImageZoomPan
  zoomLevel: number;
  setZoomLevel: React.Dispatch<React.SetStateAction<number>>;
  panOffset: { x: number; y: number };
  isDragging: boolean;
  pointerHandlers: {
    onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
    onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
    onPointerUp: () => void;
    onPointerLeave: () => void;
  };
  // Enhancement
  enhanceOpen: boolean;
  setEnhanceOpen: (open: boolean) => void;
  previewFilter: string | undefined;
  brightness: number;
  setBrightness: (v: number) => void;
  contrast: number;
  setContrast: (v: number) => void;
  colorMode: 'color' | 'grayscale' | 'document';
  onColorMode: (mode: 'color' | 'grayscale' | 'document') => void;
  sharpen: boolean;
  setSharpen: (v: boolean) => void;
  onAutoEnhance: () => void;
  onEnhanceReset: () => void;
  onEnhanceApply: () => void;
  isEnhancing: boolean;
  isEnhanceDisabled: boolean;
  /** Byte-level upload progress (0-100) while the enhanced image uploads. */
  enhanceProgress?: number | null;
  // QR detection overlay — server-persisted corners win; live decode is the
  // fallback for legacy pages without a stored bbox.
  qrStatus?: QrOverlayStatus | null;
  qrBbox?: QrBboxData | null;
  liveQrCorners?: { x: number; y: number }[] | null;
  qrLabel?: string | null;
  // Read-only embed (hides the enhance sheet trigger)
  readOnly?: boolean;
}

// ─── QrReviewImageViewer ─────────────────────────────────────────────────────

export function QrReviewImageViewer({
  mainImageBlobUrl,
  selectedPage,
  selectedPageIndex,
  totalPages,
  onPrev,
  onNext,
  zoomLevel,
  setZoomLevel,
  panOffset,
  isDragging,
  pointerHandlers,
  enhanceOpen,
  setEnhanceOpen,
  previewFilter,
  brightness,
  setBrightness,
  contrast,
  setContrast,
  colorMode,
  onColorMode,
  sharpen,
  setSharpen,
  onAutoEnhance,
  onEnhanceReset,
  onEnhanceApply,
  isEnhancing,
  isEnhanceDisabled,
  enhanceProgress = null,
  qrStatus = null,
  qrBbox = null,
  liveQrCorners = null,
  qrLabel = null,
  readOnly = false
}: QrReviewImageViewerProps) {
  const t = useTranslations('qrReview');
  const locale = useLocale();

  const enhancePanelResize = usePanelWidth('qr-review-panel', {
    defaultWidth: 380,
    min: 300,
    max: 640,
    edge: 'start'
  });

  const centerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null
  );

  // ── Fullscreen ──────────────────────────────────────────────────────────
  const toggleFullscreen = useCallback(async () => {
    if (!document.fullscreenElement) {
      await centerRef.current?.requestFullscreen();
    } else {
      await document.exitFullscreen();
    }
  }, []);

  useEffect(() => {
    const handler = () => {
      const fs = !!document.fullscreenElement;
      setIsFullscreen(fs);
      setPortalContainer(
        fs ? (centerRef.current ?? document.body) : document.body
      );
    };
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  return (
    <div
      ref={centerRef}
      className='bg-muted/40 relative flex flex-1 flex-col overflow-hidden dark:bg-slate-900'
    >
      {/* Page counter pill */}
      <div className='pointer-events-none absolute start-1/2 top-3 z-10 -translate-x-1/2'>
        <span className='rounded-full bg-black/50 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm'>
          {selectedPage ? `${selectedPage.sequence} / ${totalPages}` : '— / —'}
        </span>
      </div>

      {/* Image viewport — overflow-hidden clips the scaled content; drag to pan */}
      <div
        className={cn(
          'relative flex-1 overflow-hidden',
          zoomLevel > 100
            ? isDragging
              ? 'cursor-grabbing'
              : 'cursor-grab'
            : 'cursor-default'
        )}
        onPointerDown={pointerHandlers.onPointerDown}
        onPointerMove={pointerHandlers.onPointerMove}
        onPointerUp={pointerHandlers.onPointerUp}
        onPointerLeave={pointerHandlers.onPointerLeave}
      >
        {/* Pan layer — absolute fill so maxWidth/maxHeight are relative to the center area */}
        <div
          className='absolute inset-0 flex items-center justify-center p-8'
          style={{
            transform: `translate(${panOffset.x}px, ${panOffset.y}px)`
          }}
        >
          {mainImageBlobUrl ? (
            <div
              className='relative bg-white shadow-2xl ring-1 ring-black/10 dark:ring-white/10'
              style={{ borderRadius: '1rem' }}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={mainImageBlobUrl}
                alt={`${t('pageLabel')} ${selectedPage?.sequence}`}
                draggable={false}
                fetchPriority='high'
                decoding='async'
                style={{
                  maxHeight: '78vh',
                  maxWidth: '100%',
                  width: 'auto',
                  height: 'auto',
                  display: 'block',
                  borderRadius: '1rem',
                  transform: `scale(${zoomLevel / 100})`,
                  transformOrigin: 'center',
                  transition: isDragging ? 'none' : 'transform 0.15s ease',
                  filter: previewFilter ?? undefined
                }}
              />

              {/* Verdict tint */}
              {selectedPage?.qa_verdict !== null && (
                <div
                  className={cn(
                    'pointer-events-none absolute inset-0',
                    selectedPage?.qa_verdict === 'passed'
                      ? 'bg-green-500/10'
                      : 'bg-red-500/10'
                  )}
                />
              )}

              {/* Verdict badge */}
              {selectedPage?.qa_verdict !== null && (
                <div
                  className={cn(
                    'absolute end-3 top-3 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold text-white shadow',
                    selectedPage?.qa_verdict === 'passed'
                      ? 'bg-green-500'
                      : 'bg-red-500'
                  )}
                >
                  {selectedPage?.qa_verdict === 'passed' ? (
                    <CheckCircle2 className='h-3.5 w-3.5' />
                  ) : (
                    <XCircle className='h-3.5 w-3.5' />
                  )}
                  {selectedPage?.qa_verdict === 'passed'
                    ? t('statusQaPassed')
                    : t('statusQaFailed')}
                </div>
              )}

              {/* QR detection overlay — polygon over the printed QR + corner
                  chip. Aligned to the un-rotated image bounds; the wrapper
                  matches the image's natural size so 0..1 fraction corners
                  land on the correct pixels. */}
              <QrOverlay
                status={qrStatus}
                qrBbox={qrBbox}
                liveCorners={liveQrCorners}
                label={qrLabel}
              />
            </div>
          ) : (
            <div className='bg-card flex h-96 w-64 items-center justify-center rounded-2xl shadow-2xl'>
              <Loader2 className='text-muted-foreground h-8 w-8 animate-spin' />
            </div>
          )}
        </div>
      </div>

      {/* Prev arrow */}
      <button
        type='button'
        onClick={onPrev}
        disabled={selectedPageIndex === 0}
        className='absolute start-3 top-1/2 z-10 w-fit -translate-y-1/2 rounded-full bg-white/90 p-2.5 shadow-md transition-all hover:bg-white hover:shadow-lg disabled:opacity-30 rtl:end-3 rtl:rotate-180 dark:bg-slate-700/90 dark:text-slate-100 dark:hover:bg-slate-700'
      >
        <ChevronLeft className='h-5 w-5' />
      </button>

      {/* Next arrow */}
      <button
        type='button'
        onClick={onNext}
        disabled={selectedPageIndex === totalPages - 1}
        className='absolute end-3 top-1/2 z-10 w-fit -translate-y-1/2 rounded-full bg-white/90 p-2.5 shadow-md transition-all hover:bg-white hover:shadow-lg disabled:opacity-30 rtl:rotate-180 dark:bg-slate-700/90 dark:text-slate-100 dark:hover:bg-slate-700'
      >
        <ChevronRight className='h-5 w-5' />
      </button>

      {/* Floating toolbar */}
      <div className='absolute start-1/2 bottom-4 z-10 -translate-x-1/2 rtl:translate-x-1/2'>
        <div className='flex items-center gap-0.5 rounded-full border border-black/10 bg-white/95 px-3 py-1.5 shadow-lg backdrop-blur dark:border-white/10 dark:bg-slate-800/95'>
          <button
            type='button'
            onClick={() => setZoomLevel((z) => Math.max(25, z - 25))}
            className='hover:bg-muted rounded-full p-1.5'
            title='-25%'
          >
            <ZoomOut className='h-4 w-4' />
          </button>
          <button
            type='button'
            onClick={() => setZoomLevel(100)}
            className='hover:bg-muted w-12 rounded-md px-1 py-1 text-center text-xs font-semibold'
            title={t('zoomReset')}
          >
            {zoomLevel}%
          </button>
          <button
            type='button'
            onClick={() => setZoomLevel((z) => Math.min(300, z + 25))}
            className='hover:bg-muted rounded-full p-1.5'
            title='+25%'
          >
            <ZoomIn className='h-4 w-4' />
          </button>
          {!readOnly && <div className='bg-border mx-1.5 h-4 w-px' />}
          {!readOnly && (
            <SheetPrimitive.Root
              open={enhanceOpen}
              onOpenChange={setEnhanceOpen}
            >
              <SheetPrimitive.Trigger asChild>
                <button
                  type='button'
                  className={cn(
                    'rounded-full p-1.5 transition-colors',
                    enhanceOpen ? 'bg-blue-500 text-white' : 'hover:bg-muted'
                  )}
                  title={t('enhanceTitle')}
                >
                  <Sparkles className='h-4 w-4' />
                </button>
              </SheetPrimitive.Trigger>
              <SheetPrimitive.Portal container={portalContainer}>
                <SheetPrimitive.Content
                  style={{ width: enhancePanelResize.widthValue }}
                  className={cn(
                    'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed inset-y-0 z-50 flex h-full max-w-[90vw] flex-col gap-4 border shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500',
                    locale === 'ar'
                      ? 'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left left-0 border-e'
                      : 'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right right-0 border-s'
                  )}
                >
                  {/* `start-0` is the panel's content-facing edge in both
                      directions, so this needs no locale branch of its
                      own — unlike the slide animation above. */}
                  <PanelResizeHandle
                    className='absolute inset-y-0 start-0'
                    {...enhancePanelResize.handleProps}
                  />

                  {/* Header */}
                  <div className='flex flex-row items-center justify-between gap-2 p-4'>
                    <div className='flex items-center gap-2'>
                      <Sparkles className='text-primary h-4 w-4' />
                      <SheetPrimitive.Title className='text-foreground text-base font-semibold'>
                        {t('enhanceTitle')}
                      </SheetPrimitive.Title>
                    </div>
                    <button
                      type='button'
                      onClick={onAutoEnhance}
                      className='hover:bg-muted flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors'
                    >
                      <Sparkles className='h-3 w-3' />
                      {t('enhanceAutoEnhance')}
                    </button>
                  </div>

                  <Separator />

                  <ScrollArea className='flex-1'>
                    <div className='space-y-5 p-4'>
                      {/* Brightness */}
                      <div className='space-y-1.5'>
                        <div className='flex justify-between text-xs'>
                          <span className='font-medium'>
                            {t('enhanceBrightness')}
                          </span>
                          <span className='text-muted-foreground tabular-nums'>
                            {brightness}%
                          </span>
                        </div>
                        <input
                          type='range'
                          min={50}
                          max={200}
                          step={5}
                          value={brightness}
                          onChange={(e) =>
                            setBrightness(Number(e.target.value))
                          }
                          className='w-full accent-blue-500'
                        />
                      </div>

                      {/* Contrast */}
                      <div className='space-y-1.5'>
                        <div className='flex justify-between text-xs'>
                          <span className='font-medium'>
                            {t('enhanceContrast')}
                          </span>
                          <span className='text-muted-foreground tabular-nums'>
                            {contrast}%
                          </span>
                        </div>
                        <input
                          type='range'
                          min={50}
                          max={200}
                          step={5}
                          value={contrast}
                          onChange={(e) => setContrast(Number(e.target.value))}
                          className='w-full accent-blue-500'
                        />
                      </div>

                      {/* Color mode */}
                      <div className='space-y-1.5'>
                        <span className='text-xs font-medium'>
                          {t('enhanceGrayscale')}
                        </span>
                        <div className='flex overflow-hidden rounded-md border text-[11px]'>
                          {(
                            [
                              { mode: 'color', label: t('enhanceModeColor') },
                              {
                                mode: 'grayscale',
                                label: t('enhanceModeGrayscale')
                              },
                              {
                                mode: 'document',
                                label: t('enhanceModeDocument')
                              }
                            ] as {
                              mode: 'color' | 'grayscale' | 'document';
                              label: string;
                            }[]
                          ).map(({ mode, label }, idx) => (
                            <button
                              key={mode}
                              type='button'
                              onClick={() => onColorMode(mode)}
                              className={cn(
                                'flex-1 py-1.5 font-medium transition-colors',
                                idx !== 0 && 'border-s',
                                colorMode === mode
                                  ? 'bg-blue-500 text-white'
                                  : 'hover:bg-muted'
                              )}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Sharpen */}
                      <label className='flex cursor-pointer items-center gap-2 text-xs font-medium'>
                        <input
                          type='checkbox'
                          checked={sharpen}
                          onChange={(e) => setSharpen(e.target.checked)}
                          className='accent-blue-500'
                        />
                        {t('enhanceSharpen')}
                      </label>
                    </div>
                  </ScrollArea>

                  <Separator />

                  <div className='space-y-3 p-4'>
                    <UploadProgress value={enhanceProgress} />
                    <div className='flex gap-2'>
                      <Button
                        variant='outline'
                        size='sm'
                        className='flex-1'
                        onClick={onEnhanceReset}
                        disabled={isEnhanceDisabled}
                      >
                        {t('enhanceCancel')}
                      </Button>
                      <Button
                        size='sm'
                        className='flex-1'
                        onClick={onEnhanceApply}
                        disabled={
                          !mainImageBlobUrl || isEnhanceDisabled || isEnhancing
                        }
                      >
                        {isEnhancing ? '…' : t('enhanceApply')}
                      </Button>
                    </div>
                  </div>

                  <SheetPrimitive.Close className='ring-offset-background focus:ring-ring absolute top-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none ltr:right-4 rtl:left-4'>
                    <X className='size-4' />
                    <span className='sr-only'>{t('closePanel')}</span>
                  </SheetPrimitive.Close>
                </SheetPrimitive.Content>
              </SheetPrimitive.Portal>
            </SheetPrimitive.Root>
          )}
          <button
            type='button'
            onClick={() => void toggleFullscreen()}
            className='hover:bg-muted rounded-full p-1.5'
            title={isFullscreen ? t('exitFullscreen') : t('fullscreen')}
          >
            {isFullscreen ? (
              <Minimize2 className='h-4 w-4' />
            ) : (
              <Maximize2 className='h-4 w-4' />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
